import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import { URLS } from '@wix/da-url';
import ddt from '@wix/da-ddt';
import { getCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/selectors';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { setCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/actions';
import { popModal } from './basicActions';
import {
  confirmNo,
  confirmYes,
  doNotShowAgainChanged,
  pushBadgeModal,
} from './actionCreators';
import { closeNotificationsDropdown } from '../../SiteHeader/redux/actions';
import { messageReceived } from '../../redux/messages/action';
import { createOrOpenChatWithUser } from '../../Chat/redux/actions';
import { getCurrentModal } from './selectors';
import { ModalStackItem } from './types';
import { CloseOnConfirmOption } from '../../Popup/ConfirmationModal/types';

function* handleBadgeModal(message: any) {
  let isInsideIframe: boolean;
  try {
    isInsideIframe = window.self !== window.top;
  } catch (e) {
    isInsideIframe = true;
  }

  // badge modal could be fired from the recent messages iframe in header
  if (isInsideIframe) {
    return;
  }

  if (message.badgeName && message.userId) {
    ddt.log('shared', 'firing pushBadgeModal', message);
    yield put(
      pushBadgeModal(message.username, message.onBadgeSent, message.onClose)
    );
  }
}

function* handleGiveModal(message: any) {
  if (message.command === 'badge_receipt') {
    // Relay the message to the notifications iframe too
    const iframe: any = document.getElementById('eclipse-notifications-iframe');
    if (iframe && iframe.contentWindow) {
      // Notifications iframe is always hosted on www.deviantart.com
      // so target origin is URLS.ROOT
      iframe.contentWindow.postMessage(JSON.stringify(message), URLS.ROOT);
    }
  }
}

function* handleMessage({
  payload: message,
}: ReturnType<typeof messageReceived>) {
  switch (message.type) {
    case 'OpenBadgeModal':
      yield call(handleBadgeModal, message);
      break;
    case 'GiveModal':
      yield call(handleGiveModal, message);
      break;
    case 'OpenChat':
      yield put(
        createOrOpenChatWithUser(
          `window-message-${message.user.useridUuid}`,
          message.user
        )
      );
      if (message.source && message.source === 'messagecentre') {
        yield put(closeNotificationsDropdown());
      }
      break;
    default:
  }
}

function* handleConfirmAnswer(
  action: ReturnType<typeof confirmNo | typeof confirmYes>
) {
  const modal: ModalStackItem = yield select(getCurrentModal);
  const closeOnConfirmationType: CloseOnConfirmOption =
    modal?.options?.params?.closeOnConfirmation ?? CloseOnConfirmOption.Close;
  if (closeOnConfirmationType === CloseOnConfirmOption.DontClose) {
    return;
  }
  yield put(popModal());
  if (modal?.options?.params?.onClose) {
    modal?.options?.params?.onClose();
  }
}

function* handleDoNotShowAgainChanged(
  action: ReturnType<typeof doNotShowAgainChanged>
) {
  try {
    const dnsDb = yield select(
      getCompliantLocalStorageItem,
      CompliantLocalStorageKeys.DO_NOT_SHOW_AGAIN
    );
    let dnsItems: { [key: string]: boolean };
    if (!dnsDb) {
      dnsItems = {
        [action.payload.key]: action.payload.value,
      };
      yield put(
        setCompliantLocalStorageItem({
          key: CompliantLocalStorageKeys.DO_NOT_SHOW_AGAIN,
          value: JSON.stringify(dnsItems),
        })
      );
      return;
    }
    dnsItems = JSON.parse(dnsDb);
    dnsItems[action.payload.key] = action.payload.value;
    yield put(
      setCompliantLocalStorageItem({
        key: CompliantLocalStorageKeys.DO_NOT_SHOW_AGAIN,
        value: JSON.stringify(dnsItems),
      })
    );
  } catch (e) {
    const dnsItems = {
      [action.payload.key]: action.payload.value,
    };
    yield put(
      setCompliantLocalStorageItem({
        key: CompliantLocalStorageKeys.DO_NOT_SHOW_AGAIN,
        value: JSON.stringify(dnsItems),
      })
    );
  }
}

export default function* saga() {
  yield all([
    takeEvery(messageReceived, handleMessage),
    takeEvery(confirmYes, handleConfirmAnswer),
    takeEvery(confirmNo, handleConfirmAnswer),
    takeEvery(doNotShowAgainChanged, handleDoNotShowAgainChanged),
  ]);
}
